import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import { cardStyle, font, ink, line, radius, transition } from 'features/dashboard/tokens'
import { RAG } from 'features/dashboard/charts/scoreColour'
import TasksRemedialsExplainer from 'features/taskManagement/TasksRemedialsExplainer'
import DismissActionDialog from 'features/actions/components/DismissActionDialog'
import { getNewRemedials } from 'services/newRemedialsService'
import ActionsAPI from 'services/ActionsAPI'
import { refreshRemedialCounts } from 'features/actions/ducks/refreshRemedialCounts'
import { toast } from 'react-toastify'
import { PRIVATE_PATHS } from 'utils/paths'

const summaryCache = new Map()

const SEV = { critical: RAG.critical.fg, high: RAG.high.fg, medium: RAG.medium.fg, low: RAG.low.fg }
const sevColour = s => SEV[s] || ink.mute

const NewRemedialsCard = ({ bpId }) => {
  const navigate = useNavigate()
  const [summary, setSummary] = useState(() => summaryCache.get(bpId) || null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [dismissTarget, setDismissTarget] = useState(null)

  useEffect(() => {
    if (!bpId) return undefined
    let cancelled = false
    setSummary(summaryCache.get(bpId) || null)
    getNewRemedials(bpId)
      .then(res => {
        const data = res?.data?.data || null
        if (data) summaryCache.set(bpId, data)
        if (!cancelled) setSummary(data)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [bpId])

  const removeItem = id => setSummary(s => {
    if (!s) return s
    const item = (s.items || []).find(i => i.id === id)
    const items = (s.items || []).filter(i => i.id !== id)
    const bySeverity = { ...(s.bySeverity || {}) }
    if (item && bySeverity[item.severity] != null) {
      bySeverity[item.severity] = Math.max(0, bySeverity[item.severity] - 1)
    }
    const next = { ...s, count: Math.max(0, (s.count || 0) - 1), items, bySeverity }
    summaryCache.set(bpId, next)
    return next
  })

  const onApprove = async (it) => {
    if (busy) return
    setBusy(true)
    try {
      await ActionsAPI.approve(it.id)
      removeItem(it.id)
      toast.success('Remedial approved — now active in your remedials.')
      refreshRemedialCounts()
    } catch {
      toast.error('Could not approve this remedial. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (!summary || !summary.count) return null

  const { count, fileCount, bySeverity = {}, items = [] } = summary

  return (
    <Wrap>
      <Banner type='button' $open={open} onClick={() => setOpen(o => !o)}>
        <Icon><AutoAwesomeRoundedIcon style={{ fontSize: 18 }} /></Icon>
        <Text>
          <strong>{count} new remedial recommendation{count === 1 ? '' : 's'}</strong>
          {fileCount > 0 && <span> from {fileCount} recent upload{fileCount === 1 ? '' : 's'}</span>}
        </Text>
        <Chips>
          {(bySeverity.critical || 0) > 0 && <Chip $tint={RAG.critical.fg}>{bySeverity.critical} critical</Chip>}
          {(bySeverity.high || 0) > 0 && <Chip $tint={RAG.high.fg}>{bySeverity.high} high</Chip>}
          {(bySeverity.medium || 0) > 0 && <Chip $tint={RAG.medium.fg}>{bySeverity.medium} medium</Chip>}
        </Chips>
        <Explain onClick={e => e.stopPropagation()}><TasksRemedialsExplainer align='right' /></Explain>
        <Chevron $open={open}><ExpandMoreRoundedIcon style={{ fontSize: 20 }} /></Chevron>
      </Banner>

      {open && (
        <List>
          {items.map(it => (
            <Row key={it.id}>
              <SevDot $tint={sevColour(it.severity)} title={it.severity} />
              <RowText title={it.title}>
                <RowTitle>{it.title}</RowTitle>
                {it.sourceFile?.displayName && <RowFile>{it.sourceFile.displayName}</RowFile>}
              </RowText>
              <RowActions>
                <AddBtn type='button' disabled={busy} onClick={() => onApprove(it)}>
                  <AssignmentTurnedInRoundedIcon style={{ fontSize: 15 }} /> Approve
                </AddBtn>
                <GhostBtn type='button' title='Remove this AI find because it is not a real problem' onClick={() => setDismissTarget({ ...it, status: 'pending_review', buildingPassport: { id: bpId } })}>
                  Not a real issue
                </GhostBtn>
              </RowActions>
            </Row>
          ))}
          {count > items.length && (
            <SeeAll type='button' onClick={() => navigate(PRIVATE_PATHS.remedialsReview.replace(':bpId', bpId))}>
              Review all {count} →
            </SeeAll>
          )}
        </List>
      )}

      {dismissTarget && (
        <DismissActionDialog
          action={dismissTarget}
          open={!!dismissTarget}
          onClose={() => setDismissTarget(null)}
          onDismissed={() => { removeItem(dismissTarget.id); setDismissTarget(null) }}
        />
      )}
    </Wrap>
  )
}

export default NewRemedialsCard

const Wrap = styled.div`
  ${cardStyle}
  margin-top: 14px;
  margin-bottom: 14px;
  padding: 0;
  overflow: hidden;
  border-left: 4px solid ${({ theme }) => theme.colors.secondary};
`

const Banner = styled.button`
  font: inherit;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  &:hover { background: ${({ theme }) => `${theme.colors.secondary}08`}; }
`

const Icon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: ${radius.sm};
  background: ${({ theme }) => `${theme.colors.secondary}1A`};
  color: ${({ theme }) => theme.colors.secondary};
`

const Text = styled.span`
  font-size: ${font.body};
  color: ${ink.base};
  min-width: 0;
  strong { font-weight: 700; }
  span { color: ${ink.mute}; }
`

const Chips = styled.div`
  display: flex;
  gap: 5px;
  margin-left: auto;
  flex-shrink: 0;
`

const Chip = styled.span`
  padding: 2px 9px;
  border-radius: ${radius.pill};
  background: ${({ $tint }) => `${$tint}12`};
  border: 1px solid ${({ $tint }) => `${$tint}30`};
  color: ${({ $tint }) => $tint};
  font-size: ${font.caption};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`

const Explain = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`

const Chevron = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${ink.mute};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 180ms ease;
`

const List = styled.div`
  border-top: 1px solid ${line.base};
  display: flex;
  flex-direction: column;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  border-bottom: 1px solid ${line.base};
  &:last-child { border-bottom: none; }
`

const SevDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $tint }) => $tint};
`

const RowText = styled.div`
  flex: 1;
  min-width: 0;
`

const RowTitle = styled.div`
  font-size: ${font.body};
  font-weight: 600;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowFile = styled.div`
  font-size: ${font.caption};
  color: ${ink.mute};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 5px 11px;
  border-radius: ${radius.pill};
  border: none;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  &:hover:not(:disabled) { filter: brightness(1.06); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`

const GhostBtn = styled.button`
  min-height: 40px;
  padding: 5px 11px;
  border-radius: ${radius.pill};
  border: 1px solid ${line.base};
  background: #fff;
  color: ${ink.soft};
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  &:hover { border-color: ${line.strong}; }
`

const SeeAll = styled.button`
  padding: 9px 16px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: ${font.caption};
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`
